import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Radio, BookOpen, History, FileText, Calendar, Ticket, Users, LogOut, ArrowRight, Building2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AccountWarningBanner } from '@/components/AccountWarningBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const STATE_BADGE = {
  active: { label: 'Ativa', cls: 'bg-emerald-900/50 text-emerald-400 border-emerald-700' },
  expired: { label: 'Expirada', cls: 'bg-red-900/50 text-red-400 border-red-700' },
  scheduled: { label: 'Agendada', cls: 'bg-sky-900/50 text-sky-400 border-sky-700' },
  suspended: { label: 'Suspensa', cls: 'bg-slate-700/50 text-slate-300 border-slate-600' },
  quota_reached: { label: 'Sem quota', cls: 'bg-amber-900/50 text-amber-400 border-amber-700' },
};

export default function ClientPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [acc, sess] = await Promise.all([
        axios.get(`${API}/me/account`, { headers: authHeaders() }),
        axios.get(`${API}/sessions/my-sessions?include_archived=true`, { headers: authHeaders() }),
      ]);
      setData(acc.data);
      setEvents(sess.data);
    } catch (e) {
      if (e.response?.status === 401) { navigate('/login'); return; }
      toast.error('Erro ao carregar o portal');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!token || !u) { navigate('/login'); return; }
    const parsed = JSON.parse(u);
    setUser(parsed);
    if (parsed.role === 'admin') { navigate('/admin'); return; }
    load();
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-cyan-400" /></div>;
  }

  const account = data?.account;
  const blocked = data?.blocked;
  const badge = STATE_BADGE[account?.computed_state] || STATE_BADGE.active;
  const recentEnded = events.filter((e) => e.status === 'archived' || e.status === 'stopped').slice(0, 5);

  const actions = [
    { key: 'create', title: 'Criar Evento', desc: 'Inicie uma nova sessão de tradução ao vivo', icon: Radio, onClick: () => navigate('/host'), disabled: blocked, accent: 'from-cyan-600 to-blue-600' },
    { key: 'events', title: 'Os Meus Eventos', desc: 'Histórico, transcrições e relatórios', icon: History, onClick: () => navigate('/history'), accent: 'from-slate-700 to-slate-600' },
    { key: 'glossaries', title: 'Glossários', desc: 'Gerir a terminologia da sua organização', icon: BookOpen, onClick: () => navigate('/glossaries'), accent: 'from-violet-600 to-fuchsia-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="client-portal">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src="/logo-dih.png" alt="Do IT Happen" className="h-10" />
            <div>
              <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold" data-testid="portal-title">Portal do Cliente</h1>
              <p className="text-xs text-slate-500">{account?.name || 'A minha conta'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && <span className="text-sm text-slate-400 mr-2 hidden sm:inline">{user.name}</span>}
            <Button variant="ghost" onClick={logout} className="text-red-400 hover:text-red-300" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>

        {/* Warnings */}
        <AccountWarningBanner warnings={data?.warnings} className="mb-6" />

        {/* Account status card */}
        {account ? (
          <Card className="bg-gradient-to-br from-slate-900 to-slate-900/60 border-slate-800 mb-8" data-testid="portal-account-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="font-['Manrope'] font-bold text-lg">{account.name}</p>
                    <p className="text-xs text-slate-500">{account.plan_label || 'Plano DO IT Translate'}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badge.cls}`} data-testid="portal-account-state">{badge.label}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/60 rounded-lg p-3" data-testid="portal-validity">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Validade</p>
                  <p className="text-sm text-white mt-1">{account.valid_from || '—'} → {account.valid_until || '∞'}</p>
                  {account.days_remaining !== null && account.days_remaining !== undefined && (
                    <p className={`text-[11px] mt-0.5 ${account.days_remaining <= 3 ? 'text-amber-400' : 'text-slate-500'}`}>
                      {account.days_remaining < 0 ? 'expirada' : account.days_remaining === 0 ? 'expira hoje' : `faltam ${account.days_remaining} dia(s)`}
                    </p>
                  )}
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3" data-testid="portal-events">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><Ticket className="w-3 h-3" /> Eventos</p>
                  <p className="text-sm text-white mt-1">{account.events_used}{account.max_events != null ? ` / ${account.max_events}` : ''}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{account.max_events != null ? `${account.events_remaining} restante(s)` : 'ilimitado'}</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3" data-testid="portal-participants">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Participantes / evento</p>
                  <p className="text-sm text-white mt-1">{account.max_participants_per_event ?? 'ilimitado'}</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Estado</p>
                  <p className="text-sm text-white mt-1">{blocked ? 'Bloqueada' : 'Operacional'}</p>
                  {blocked && <p className="text-[11px] text-red-400 mt-0.5">{account.block_reason || 'contacte a DO IT'}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-900 border-amber-800/60 mb-8"><CardContent className="pt-6">
            <p className="text-sm text-amber-300" data-testid="portal-no-account">A sua conta ainda não está configurada. Contacte a DO IT HAPPEN.</p>
          </CardContent></Card>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {actions.map((a) => (
            <button
              key={a.key}
              onClick={a.disabled ? undefined : a.onClick}
              disabled={a.disabled}
              className={`text-left rounded-xl border border-slate-800 p-5 transition-all group ${a.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-slate-600 hover:-translate-y-0.5 bg-slate-900'}`}
              data-testid={`portal-action-${a.key}`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${a.accent} flex items-center justify-center mb-3`}>
                <a.icon className="w-5 h-5 text-white" />
              </div>
              <p className="font-['Manrope'] font-bold text-white flex items-center gap-2">
                {a.title} <ArrowRight className="w-4 h-4 text-slate-500 group-hover:translate-x-1 transition-transform" />
              </p>
              <p className="text-xs text-slate-400 mt-1">{a.desc}</p>
            </button>
          ))}
        </div>

        {/* Recent ended events with report links */}
        <Card className="bg-slate-900 border-slate-800" data-testid="portal-recent-events">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="font-['Manrope'] font-bold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Eventos recentes com relatório</p>
              <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="text-slate-400 text-xs">Ver todos</Button>
            </div>
            {recentEnded.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center" data-testid="portal-no-recent">Ainda não há eventos terminados. Crie o seu primeiro evento.</p>
            ) : (
              <div className="space-y-2">
                {recentEnded.map((e) => (
                  <div key={e.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-4 py-2.5" data-testid={`portal-event-${e.code}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{e.name || 'Evento'} <span className="text-slate-500 font-mono text-xs">· {e.code}</span></p>
                      <p className="text-[11px] text-slate-500">{new Date(e.created_at).toLocaleString('pt-PT')}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/report/${e.id}`)} className="border-cyan-800 text-cyan-400 hover:bg-cyan-950 shrink-0" data-testid={`portal-report-${e.code}`}>
                      <FileText className="w-3.5 h-3.5 mr-1" /> Relatório
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
